import { SxProps } from '@mui/material';
import { lightTheme, globalSectionPadding } from '../../theme/index';
import { boxShadow } from '../../theme/colorPalette';

const { palette, spacing } = lightTheme;

const greaterGlobalSectionPadding = JSON.parse(
  JSON.stringify(globalSectionPadding).replace(/rem/g, '')
);

const searchBtn = {
  border: `0.063rem solid ${palette.grey[300]}`,
  width: '1.5rem',
  height: '2.5rem',
  '& svg': {
    width: '2rem',
    color: palette.grey.main
  },
  minWidth: '2rem',
  padding: '1.3rem',
  color: palette.grey.main
};

const styles: Record<string, SxProps> = {
  mainBox: {
    width: '100%'
  },

  minw120: {
    minWidth: 120
  },

  boxShadow200: {
    boxShadow: boxShadow[200]
  },

  // properties details styles
  propertiesNo: {
    color: palette.darkestBlack.main,
    alignSelf: 'flex-start',
    fontWeight: 600
  },
  propertiesSearchSubtitle: {
    marginTop: spacing(0.2),
    color: palette.grey['400'],
    fontWeight: 600
  },
  propertiesSubtitle: {
    color: palette.grey['400'],
    fontWeight: 500
  },

  // TopAutocompleteBox
  topBox: {
    backgroundColor: palette.primary['50'],
    '& > div': {
      paddingX: spacing(1),
      paddingTop: spacing(1),
      paddingBottom: spacing(0.5),
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    '#filters-array': {
      transition:
        '.2s max-height cubic-bezier(0, 0.65, 0.76, 1.07), .15s opacity ease-out, .2s margin cubic-bezier(0, 0.65, 0.76, 1.07)',
      overflow: 'visible',
      maxHeight: '100vh',
      paddingLeft: spacing(1),
      paddingY: '0rem',
      opacity: 1
    },

    [lightTheme.breakpoints.up('md')]: {
      position: 'sticky',
      top: '5.625rem',
      zIndex: 1,
      boxShadow: 'none',
      transition: '.1s box-shadow ease-out',

      '&.isStuck': {
        boxShadow: `${palette.grey.A100} 0 0.5rem 1.75rem 0`,

        '#filters-array': {
          maxHeight: 0,
          opacity: 0,
          margin: 0,
          paddingY: 0
        }
      }
    }
  },

  filterBox: {
    width: '100%',
    display: 'flex',
    gap: {
      xs: spacing(0.5),
      md: spacing(1)
    }
  },

  filterSearchBox: {
    display: 'flex',
    gap: {
      xs: spacing(0.5),
      md: spacing(1)
    },
    width: '100%',
    maxWidth: {
      xs: '100%',
      md: '80rem'
    },
    flexDirection: {
      xs: 'column',
      lg: 'row'
    }
  },

  bedroomFilterBox: {
    display: 'flex',
    gap: {
      xs: spacing(0.5),
      md: spacing(1)
    },
    width: '50%'
  },

  // Autocomplete Field
  autocompleteContainer: {
    display: 'flex',
    alignItems: 'center',
    border: `1px solid ${palette.primary.A100}`,
    borderRadius: '0.25rem',
    paddingRight: '0.3rem',
    boxShadow: `0rem 0.25rem 2.5rem ${palette.grey.A100}`,
    backgroundColor: palette.grey['50'],
    width: '100%',
    maxWidth: {
      md: '40rem'
    }
  },

  autocompleteBox: {
    width: '100%',
    '& > div': {
      minWidth: {
        xs: '10rem',
        sm: '17rem',
        md: '20rem'
      }
    },
    '& .MuiAutocomplete-popper': {
      'ul::-webkit-scrollbar': {
        width: '0.5rem'
      },

      'ul::-webkit-scrollbar-track': {
        background: palette.primary['200'],
        borderRadius: '2.5rem'
      },

      'ul::-webkit-scrollbar-thumb': {
        background: palette.primary.main,
        borderRadius: '2.5rem'
      }
    }
  },

  autocomplete: {
    minWidth: '20rem',
    '& fieldset': {
      border: 'none'
    },
    '& button.MuiButtonBase-root': {
      visibility: 'visible'
    }
  },

  autocompleteDropdown: {
    display: 'flex',
    gap: '1.5rem',
    py: '0.75rem',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      border: `0.01rem solid ${palette.grey['100']}`,
      width: '30rem',
      bottom: '-0.375rem'
    }
  },

  mapIcon: {
    '& svg': {
      fill: palette.primary.main
    }
  },

  // styles for last search-btn
  searchBtn: {
    backgroundColor: palette.primary.main,
    minWidth: '1rem',
    '& svg': {
      width: '2rem',
      color: palette.grey['50']
    },
    '&:hover': {
      opacity: '0.8',
      backgroundColor: palette.primary.main
    }
  },

  topLeftControls: {
    gap: '1.25rem',
    display: 'flex',
    alignContent: 'center'
  },

  badge: {
    span: {
      top: { xs: '0', md: '0.75rem' },
      width: '1.7rem'
    }
  },

  filterBtn: {
    gap: '0.25rem',
    color: palette.primary.main,
    minWidth: {
      xs: '0',
      ss: '4rem'
    },
    height: '3.5rem',
    backgroundColor: {
      xs: palette.grey['50'],
      sm: palette.transparent.main
    },
    '& > .btn-label': {
      display: {
        xs: 'none',
        sm: 'block'
      }
    }
  },

  createAlertBigBox: {
    display: {
      xs: 'none',
      sm: 'block'
    }
  },
  createAlertSmallBox: {
    display: {
      xs: 'block',
      sm: 'none'
    }
  },

  createAlertBtn: {
    background: palette.grey['50'],
    height: '3.5rem'
  },

  searchTextError: {
    pl: {
      xs: `${Number(greaterGlobalSectionPadding.xs) + 0.938}rem`,
      md: `${Number(greaterGlobalSectionPadding.md) + 0.938}rem`
    }
  },

  // BottomBox
  bottomBox: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    py: '1rem',
    px: {
      xs: spacing(1),
      md: spacing(1)
    }
  },

  resultsLeftPanel: {
    maxWidth: {
      xs: '70%',
      md: 'none'
    }
  },

  resultsTextBox: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    flexDirection: 'row'
  },

  supportTextBox: {
    display: 'block',
    alignItems: 'center',
    WebkitTextStrokeWidth: '0.02rem',
    my: spacing(0.2)
  },

  bottomLayout: {
    flexDirection: { xs: 'column-reverse', sm: 'row-reverse' },
    marginLeft: {
      xs: 0,
      sm: spacing(0.5)
    }
  },

  // Bottom-left-toggle-sorting-controls
  toggleSortingControlBox: {
    display: 'flex',
    gap: spacing(1),
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  partition: {
    borderLeft: `0.125rem solid ${palette.grey['300']}`,
    height: '1.375rem',
    display: {
      xs: 'none',
      sm: 'block'
    }
  },

  mapPartition: {
    borderLeft: `0.125rem solid ${palette.grey['300']}`,
    height: '1.375rem',
    display: {
      xs: 'none',
      sm: 'none',
      md: 'none',
      lg: 'block'
    }
  },

  // VideoTour Box
  videoTourBox: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiSwitch-thumb': {
      // width: '0.5rem',
      // height: '0.5rem'
    },
    '& span:nth-of-type(2)': {
      color: palette.grey.main
    },
    '& svg': {
      fontSize: '1rem',
      display: {
        xs: 'none',
        sm: 'block'
      },
      '& svg': {
        fill: palette.grey.main
      }
    },
    paddingBottom: {
      xs: spacing(1),
      sm: 0
    }
  },

  // Sorting Block
  sortingDropdown: {
    cursor: 'pointer',
    display: {
      xs: 'none',
      sm: 'flex'
    },
    alignItems: 'center',
    '& .MuiSelect-select': {
      // Rob - Try to find a way to remove this !important tag
      paddingRight: '0 !important'
    },
    '& .MuiOutlinedInput-root': {
      color: palette.grey.main
    },
    '& .customPopoverPointer': {
      cursor: 'pointer'
    },
    '& svg': {
      fontSize: '1rem',
      '& svg': {
        fill: palette.grey.main
      }
    },
    '& fieldset': {
      border: 'none'
    }
  },

  // View Selection Box
  selectViewBox: {
    display: {
      xs: 'none',
      sm: 'none',
      md: 'none',
      lg: 'flex'
    },
    alignItems: 'center',
    '& svg': {
      fontSize: '1rem',
      '& svg': {
        fill: palette.grey.main
      }
    },
    '& .MuiOutlinedInput-root': {
      color: palette.grey.main,
      '& .MuiOutlinedInput-input': {
        color: palette.grey.main
      },
      '&:hover': {
        backgroundColor: palette.grey['50']
      },
      '&:focus': {
        backgroundColor: palette.grey['50']
      }
    },
    '& fieldset': {
      border: 'none'
    }
  },

  // Create Alert Box
  alterBoxTitle: {
    fontWeight: '700',
    color: palette.primary.main,
    textAlign: 'center'
  },
  alterBoxSubtitle: {
    marginTop: spacing(1),
    marginBottom: spacing(2),
    fontWeight: '500',
    color: palette.grey.main,
    textAlign: 'center'
  },

  bottomBoxMain: {
    '.MuiTypography-root': {
      fontWeight: '700'
    },
    position: 'fixed',
    bottom: '0',
    left: '0',
    display: {
      xs: 'flex',
      sm: 'none'
    },
    justifyContent: 'center',
    zIndex: '4',
    width: '100%',
    py: {
      xs: '0.5rem',
      ss: 'none'
    }
  },

  bottomInternalBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    backgroundColor: palette.lightishAqua.main
  },

  sortingDropdownBoxSml: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.5rem',
    gap: '0.25rem',
    borderRight: {
      md: `0.125rem solid ${palette.lightGray.main}`,
      xs: 'none'
    },
    '& svg': {
      fontSize: '1rem',
      '& svg': {
        fill: palette.grey.main
      }
    },
    '& fieldset': {
      border: 'none'
    },
    '.MuiFormControl-root': {
      minWidth: '0'
    },
    '.MuiSelect-select': {
      // Need for !important tag: not overriding MUI class
      paddingRight: '1rem !important'
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '1rem',
      fontWeight: '900',
      lineHeight: '1.25rem',
      color: palette.grey.main,
      '& > div': {
        color: palette.grey.main,
        padding: '0',
        textAlign: 'center',
        whiteSpace: 'pre-wrap',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.75rem',
        // Need for !important tag: not overriding MUI class
        paddingRight: '0 !important'
      }
    }
  },

  viewDropdownBoxSml: {
    display: { sm: 'none', xs: 'none', md: 'flex' },
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.5rem 0 0.5rem 0.5rem',
    borderRight: `0.125rem solid ${palette.lightGray.main}`,
    '& svg': {
      fontSize: '1rem',
      right: '0.25rem',
      '& svg': {
        fill: palette.grey.main
      }
    },
    '& fieldset': {
      border: 'none'
    },
    '.MuiFormControl-root': {
      minWidth: '0'
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '1rem',
      fontWeight: '900',
      lineHeight: '1.25rem',
      color: palette.grey.main,
      '& > div': {
        color: palette.grey.main,
        padding: '0',
        textAlign: 'center',
        whiteSpace: 'pre-wrap',
        display: 'flex',
        alignItems: 'center',
        fontSize: '0.75rem',
        // Need for !important tag: not overriding MUI class
        paddingRight: '1.2rem !important'
      }
    }
  },

  smallFilterButton: {
    background: palette.lightishAqua.main,
    gap: '0.25rem',
    height: '3.5rem',
    color: palette.grey.main,
    px: {
      xs: '0.8rem',
      ss: '1.125rem'
    }
  },

  iconSmall: {
    svg: {
      fill: palette.grey.main
    }
  },

  closeIcon: {
    color: palette.primary.main,
    fontSize: '1rem'
  },

  narrowResultsDesktopBtn: {
    display: { xs: 'none', md: 'block' }
  },
  narrowResultsMobBtn: {
    display: { xs: 'block', md: 'none' },
    my: spacing(0.2),
    width: '100%'
  },
  typesBox: {
    marginTop: spacing(0.5),
    gap: spacing(0.5),
    display: 'flex'
  },
  alertType: {
    fontWeight: '500',
    padding: spacing(0.5),
    borderRadius: '0.25rem',
    textAlign: 'center',
    justifyItems: 'center',
    cursor: 'pointer',
    width: '100%'
  },
  selectedType: {
    backgroundColor: palette.primary.light,
    color: palette.primary[50]
  },
  unSelectedType: {
    color: palette.grey.main,
    border: `0.063rem solid ${palette.secondary[600]}`
  },
  daysBox: {
    marginTop: spacing(1)
  },
  alertBtn: {
    width: '100%',
    textTransform: 'inherit'
  },
  progressElement: {
    backgroundColor: palette.primary.main,
    borderRadius: '50%',
    padding: {
      xs: '1rem',
      md: '1.4rem'
    },
    color: palette.grey['50'],
    display: 'flex'
  },
  progressBar: {
    height: '0.125rem',
    backgroundColor: palette.primary.main,
    width: '100%',
    marginTop: {
      xs: '1.5rem',
      md: '2.2rem'
    }
  },
  unAddedProgressElement: {
    border: `1px solid ${palette.secondary['600']}`,
    backgroundColor: palette.primary['400'],
    color: palette.primary.main
  },
  elementLabel: {
    textAlign: 'center',
    paddingTop: spacing(0.5)
  },
  MuiIcon: {
    fontSize: {
      xs: 20,
      md: 30
    }
  },
  primaryButton: {
    marginTop: spacing(1)
  },
  secondaryButton: {
    marginTop: spacing(0.5),
    borderColor: palette.secondary['600']
  },
  loadingBox: {
    position: 'absolute',
    left: '50%',
    top: '40%',
    transform: 'translate(-50%, -50%)'
  },
  mainLoadingBox: {
    backgroundColor: palette.grey.main,
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    opacity: 0.2
  },
  mapViewDropdownBoxSml: {
    display: {
      xs: 'none',
      md: 'flex'
    }
  },
  searchBtnOnDrawAreaPage: {
    ...searchBtn,
    backgroundColor: palette.grey[200]
  },
  propertiesFromDrawSubtitle: {
    color: palette.grey.main,
    fontSize: '.85rem',
    mb: '0.5rem',
    textTransform: ''
  },
  propertiesMbSubtitle: {
    display: {
      xs: 'block',
      md: 'none'
    }
  },
  propertiesDesktopSubtitle: {
    display: {
      xs: 'none',
      md: 'block'
    }
  },
  searchBtnOnMapPage: {
    ...searchBtn,
    '& svg': {
      width: '2rem',
      color: palette.grey[50]
    }
  },
  travelSearchSubtitle: {
    marginTop: '0.2rem',
    color: palette.grey.main
  },
  selectComponent: {
    cursor: 'pointer'
  }
};

export default styles;
